import { useState, useEffect } from 'react';
import { deviceService } from '../services/deviceService';

export const useDeviceManagement = (currentUser) => {
  // Organization and loading state
  const [organizationData, setOrganizationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Device list table state
  const [devices, setDevices] = useState([]);
  const [totalDevicesInOrg, setTotalDevicesInOrg] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingDevices, setLoadingDevices] = useState(true);
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchOrganizationData = async () => {
    // Skip if user doesn't have organization access
    if (!currentUser?.organizationId && currentUser?.role !== 'admin') {
      setError('No organization access available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Use the user's actual organization ID
      const orgId = currentUser.organizationId;
      if (orgId) {
        console.log(`Fetching organization data for org ID: ${orgId}`);
        const data = await deviceService.fetchOrganizationData(orgId);
        setOrganizationData(data);
      } else {
        console.log('User has no organization ID - system admin mode');
        // For system admins, we might need different logic
        setOrganizationData({
          organization: { name: 'System Administration' },
          networks: [],
          totalNetworks: 0,
          totalDevices: 0
        });
      }
      await fetchDevicesForOrganization();
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError(`Failed to fetch organization data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevicesForOrganization = async () => {
    setLoadingDevices(true);
    try {
      // Backend now handles organization filtering automatically
      const data = await deviceService.fetchDevicesForOrganization(
        currentUser?.organizationId, // Still passed for compatibility
        rowsPerPage,
        page * rowsPerPage
      );
      setDevices(data.devices || []);
      setTotalDevicesInOrg(data.total || 0);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
      setDevices([]);
      setTotalDevicesInOrg(0);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const refreshData = () => {
    fetchOrganizationData();
  };

  useEffect(() => {
    if (currentUser) {
      fetchOrganizationData();
    }
  }, [page, rowsPerPage, currentUser]);

  return {
    // State
    organizationData,
    loading,
    error,
    devices,
    totalDevicesInOrg,
    page,
    rowsPerPage,
    loadingDevices,
    snackbar,
    
    // Actions
    handleChangePage,
    handleChangeRowsPerPage,
    showSnackbar,
    hideSnackbar,
    refreshData,
    fetchDevicesForOrganization
  };
}; 